import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { IAction, IAppReduxStore } from '../../store';
import { urlSpecialRunReleaseLetter } from '../../../Constants';
import { apiFetch } from '../../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IProjectDocumentAddReleaseLetterState = {
    isDialogOpen: false,
    isLoading: false,
    error: null,
    isRequestSent: false,
    numberOfDocumentsSent: 0,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {
    openReleaseLetterDialog(state: IProjectDocumentAddReleaseLetterState) {
        state.isDialogOpen = true;
        state.isRequestSent = false;
    },
    closeReleaseLetterDialog(state: IProjectDocumentAddReleaseLetterState) {
        state.isDialogOpen = false;
    },
    resetError(state: IProjectDocumentAddReleaseLetterState) {
        state.error = null;
    },
};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Async function to add release documents to documents.
     * @param config - Object that contains the project database's  replicaId and text to return.
     */
    sendReleaseLetter: async (
        config: IProjectDocumentAddReleaseLetterConfig,
        thunkApi,
    ): Promise<void> => {
        try {
            const { replicaId, text } = config;
            const { data: documents } = thunkApi.getState().projectDocuments.info;

            const formData = new FormData();
            formData.append('additionalInfo', text);
            formData.append('universalIds', documents.map(item => item.universalId).join('|'));

            const request: RequestInit = {
                method: 'POST',
                headers: { replicaId: replicaId },
                body: formData,
            };

            const response = await apiFetch(urlSpecialRunReleaseLetter, request);

            return response.ok
                ? Promise.resolve()
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectDocumentAddReleaseLetterState>) => {
        /**
         * Reducers for the 'getProjectDocumentsAddReleaseLetter' thunk.
         */
        builder.addCase(
            sendReleaseLetter.pending,
            (state: Draft<IProjectDocumentAddReleaseLetterState>) => {
                state.isLoading = true;
                state.error = null;
            },
        );
        builder.addCase(
            sendReleaseLetter.fulfilled,
            (state: Draft<IProjectDocumentAddReleaseLetterState>) => {
                state.isLoading = false;
                state.error = null;
                state.isRequestSent = true;
            },
        );
        builder.addCase(
            sendReleaseLetter.rejected,
            (
                state: Draft<IProjectDocumentAddReleaseLetterState>,
                action: IAction<SerializedError>,
            ) => {
                state.isLoading = false;
                state.error = action.error;
            },
        );
    },
};

/**
 * Expose thunk functions as exports.
 */
export const sendReleaseLetter = createAsyncThunk(
    urlSpecialRunReleaseLetter,
    thunks.sendReleaseLetter,
);

/**
 * Interface that describes the project document's add release document state.
 */
export interface IProjectDocumentAddReleaseLetterState {
    isDialogOpen: boolean;
    isLoading: boolean;
    error: SerializedError;
    isRequestSent: boolean;
    numberOfDocumentsSent: number;
}

/**
 * Interface that describes the document's details config.
 */
export interface IProjectDocumentAddReleaseLetterConfig {
    replicaId: string;
    text: string;
}

/**
 * Create the redux slice.
 */
export const projectDocumentAddReleaseLetter = createSlice({
    name: 'projectDocumentsAddReleaseLetter',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export functions from slice actions.
 */
export const { openReleaseLetterDialog, closeReleaseLetterDialog, resetError } =
    projectDocumentAddReleaseLetter.actions;

/**
 * Export slice reducer as default.
 */
export default projectDocumentAddReleaseLetter.reducer;
